import { InternalAxiosRequestConfig } from 'axios'
import { jwtDecode } from 'jwt-decode'

import config from '@app/config'
import { Jwt } from '@app/models'
import { getProfileFromCookie } from '@app/store/auth'
import {
  getSourceUrlQueryParam,
  getURLTokenHashFromQuery,
  sendEventLogout,
} from '@app/utils'
import { DEFAULT_CULTURE } from '@app/utils/regional'

export const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') {
    return null
  }

  const cookies = document.cookie.split(';').map((cookie) => cookie.trim())
  const cookie = cookies.find((cookie) => cookie.startsWith(`${name}=`))
  return cookie ? cookie.substring(name.length + 1) : null
}

export const setCookie = (
  name: string,
  value: string,
  hours: number = 1,
  secondLevelDomain: boolean = false
) => {
  if (typeof document === 'undefined') {
    return
  }

  const date = new Date()
  date.setTime(date.getTime() + hours * 60 * 60 * 1000)

  const expires = `expires=${date.toUTCString()}`
  const hostname = document.location.hostname
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1'

  let domain = ''

  if (!isLocalhost && config.identity.isBackOffice) {
    if (secondLevelDomain) {
      const parts = hostname.split('.')
      if (parts.length >= 2) {
        const baseDomain = parts.slice(-2).join('.')
        domain = `domain=.${baseDomain};`
      }
    } else {
      domain = `domain=${hostname};`
    }
  }

  document.cookie = `${name}=${value}; ${expires}; path=/; ${domain}`
}

export const deleteCookie = (name: string) => {
  if (typeof document === 'undefined') {
    return
  }

  const domain =
    document.location.hostname !== 'localhost' && config.identity.isBackOffice
      ? `domain=${document.location.hostname};`
      : ''
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; ${domain}`
}

export const clearCookies = () => {
  deleteCookie(config.variables.b2b_access_token)
  deleteCookie(config.variables.b2c_access_token)
  deleteCookie(config.variables.roles)

  deleteCookie(config.variables.url_token)
  deleteCookie(config.variables.b2b_refresh_token)
  deleteCookie(config.variables.ojwt)
  deleteCookie(config.variables.orefresh_token)

  deleteCookie('countryIso')
  deleteCookie('lang')

  deleteCookie('hubspotutk')
  deleteCookie('hsfirstvisit')
  deleteCookie('returnurl')

  sessionStorage.clear()

  localStorage.removeItem('gaCouponId')
  localStorage.removeItem('olsCouponId')
}

export const logout = async (
  conf: InternalAxiosRequestConfig | null = null,
  redirectUrl: string | null = null
) => {
  if (conf) {
    const controller = new AbortController()
    conf.signal = controller.signal
    controller.abort('User is logged out, request cancelled.')
  }

  sessionStorage.removeItem('appLoading')
  clearCookies()

  if (config.identity.isWebView) {
    sendEventLogout()
    return
  }

  window.location.href = logoutUrl(redirectUrl)
}

export const identityUrl = () => {
  const { pathname, search } = window.location
  const params = new URLSearchParams(search)

  params.delete('token')
  params.delete('sourceUrl')

  const newSearch = params.toString() ? `?${params.toString()}` : ''

  const state = pathname + newSearch

  const token = getURLTokenHashFromQuery()

  const baseUrl =
    config.identity.identityUrl ||
    window.location.origin
      .replace('dashboard', 'id')
      .replace('my', 'id')
      .replace('app', 'id')

  const profile = getProfileFromCookie()
  const sourceUrlQueryParam = getSourceUrlQueryParam()
  const acr_values =
    !token && profile
      ? `&acr_values=${encodeURIComponent(`profile_id:${profile.id} code:${profile.code}`)}`
      : ''

  const urltoken = token ? `&urltoken=${token}` : ''
  return (
    `${baseUrl}/connect/authorize` +
    `?client_id=${config.identity.clientId}` +
    `${acr_values}` +
    `${urltoken}` +
    `${sourceUrlQueryParam}` +
    '&scope=openid%20offline_access' +
    '&response_type=code' +
    '&response_mode=form_post' +
    `&redirect_uri=${config.identity.defaultReturnUrl}` +
    `&state=${window.btoa(state)}`
  )
}

export const logoutUrl = (redirectUrl: string | null = null) => {
  if (!config.identity.isBackOffice) {
    const appUrl = config.identity.dashboardUrl || config.identity.identityUrl
    const returnUrl = encodeURIComponent(
      redirectUrl ?? `${config.identity.website}/logout`
    )

    return `${appUrl}/logout?returnUrl=${returnUrl}`
  }

  const returnurl = encodeURIComponent(redirectUrl ?? identityUrl())
  return `${config.identity.identityUrl}/logout?state=Lw=&returnurl=${returnurl}`
}

export const isTokenExpired = (token: string | null) => {
  if (!token) {
    return true
  }

  const tokenData = jwtDecode(token) as Jwt
  const expirationTime = new Date(tokenData.exp)
  const serverTime = new Date(
    new Date().toLocaleString(DEFAULT_CULTURE, { timeZone: 'Europe/Moscow' })
  )

  if (expirationTime > serverTime) {
    console.log('Token expired')
  }

  return expirationTime > serverTime
}

export const getTokenFromId = async () => {
  const link = identityUrl()
  window.location.href = link
}
